import type { StorefrontUnitsLeftDto } from "@tripcart/shared";

import { fetchWithTimeout } from "@/lib/fetch-with-timeout";

export interface UnitsLeftRequest {
  experienceId: string;
  packageId: string;
  /** `YYYY-MM-DD`. */
  date: string;
  /** The departure's 24-hour `HH:MM`, for a timed tour. Without it a
   *  time-slot resource is counted over the whole day. */
  startTime?: string | null;
  priceCategoryIds?: readonly string[];
  experienceAddonIds?: readonly string[];
}

/**
 * How many of each linked rental and add-on one departure still has, or null
 * when the answer did not arrive. Null means "no limit known", never "sold
 * out": checkout still refuses an over-capacity booking (`UNITS_EXHAUSTED`).
 */
export async function fetchUnitsLeft(
  req: UnitsLeftRequest,
): Promise<StorefrontUnitsLeftDto | null> {
  const query = new URLSearchParams({
    experienceId: req.experienceId,
    packageId: req.packageId,
    date: req.date,
  });
  if (req.startTime) query.set("startTime", req.startTime);
  if (req.priceCategoryIds?.length)
    query.set("priceCategoryIds", req.priceCategoryIds.join(","));
  if (req.experienceAddonIds?.length)
    query.set("experienceAddonIds", req.experienceAddonIds.join(","));
  try {
    const res = await fetchWithTimeout(
      `/api/checkout/units-left/?${query}`,
      {},
      10_000,
    );
    if (!res.ok) return null;
    const body = (await res.json()) as {
      success: boolean;
      data: StorefrontUnitsLeftDto;
    };
    return body.success ? body.data : null;
  } catch {
    return null;
  }
}

/** The answer as one lookup per line id. */
export function unitsLeftById(
  dto: StorefrontUnitsLeftDto | null,
): Record<string, number> {
  if (!dto) return {};
  const out: Record<string, number> = {};
  for (const c of dto.categories) out[c.priceCategoryId] = c.unitsLeft;
  for (const a of dto.addons) out[a.experienceAddonId] = a.unitsLeft;
  return out;
}
